// @LTG-ID: data/journals/due-diligence-selling-a-business
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 19 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "due-diligence-selling-a-business",
    FRONTMATTER: {
        number: "56",
        section: "Deal Process",
        sectionHref: "/journals#deal-process",
        sectionAnchor: "deal-process",
        title: "What Is Due Diligence When Selling a Business? A Seller's Survival Guide",
        deck: "Due diligence is the buyer's structured verification of everything about a business before closing: financial records, taxes, contracts, customers, employees, equipment, and legal standing.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-04-10",
        publishedHuman: "April 10, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 424,
        timeRequired: "PT3M",
        keywords: "due diligence selling a business, business sale due diligence checklist, what do buyers check when buying a business, due diligence process",
        articleSection: "Deal Process",
        articleBodyPreview: "Due diligence is the buyer's structured verification of everything about a business before closing: financial records, taxes, contracts, customers, employees, equipment, and legal standing. It typically runs 30 to 90 days after a letter of intent. Sellers who prepare organized documentation in advance protect their price and their timeline. The letter of intent feels like the finish line. It is actually the starting gun. Between an accepted offer and a closing sits due diligence, the stretch whe",
        complianceNote: "This article is educational and does not constitute legal or financial advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-long-does-it-take-to-sell-a-business",
        "asset-sale-vs-stock-sale",
        "clean-financial-records-business-value"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "What Is Due Diligence in a Business Sale? A Seller's Guide",
        canonical: "https://letstalkgrowth.com/journals/due-diligence-selling-a-business",
        ogImage: "/journals/og/due-diligence-selling-a-business.jpg",
    },
    BODY_MARKDOWN: `Due diligence is the buyer's structured verification of everything about a business before closing: financial records, taxes, contracts, customers, employees, equipment, and legal standing. It typically runs 30 to 90 days after a letter of intent. Sellers who prepare organized documentation in advance protect their price and their timeline.

The letter of intent feels like the finish line. It is actually the starting gun. Between an accepted offer and a closing sits due diligence, the stretch where deals are confirmed, repriced, or lost, and where preparation pays its largest dividend.

## What the buyer is actually doing

Diligence is not suspicion; it is verification, and often a lender's requirement as much as the buyer's. The buyer's team is answering three questions. Are the earnings real? Does the business's value transfer with the sale? And are there liabilities hiding in the walls? The work divides into predictable tracks:

- **Financial.** Statements, tax returns, bank records, and the add-back schedule, reconciled against each other. On mid-sized deals, a formal quality of earnings review is increasingly standard.
- **Legal.** Entity records, contracts, leases, licenses, insurance, litigation history, and whether key agreements can be assigned to a new owner.
- **Operational.** Equipment condition, inventory reality, supplier terms, and the systems documentation covered elsewhere in this series.
- **Customers and revenue.** Concentration analysis, contract review, retention history, and sometimes, carefully and late in the process, confidential customer conversations.
- **People.** Roster, compensation, tenure, benefit obligations, and the buyer's central worry: who stays after closing?

## Where sellers get hurt

Three patterns account for most diligence damage. **Surprises**: anything material the buyer discovers rather than being told, a tax notice, a lapsed license, a customer already wobbling, costs more than the same fact disclosed early, because discovery destroys trust. **Delay**: every document that takes two weeks to produce extends a process during which financing terms, buyer enthusiasm, and business performance can all decay. **Distraction**: sellers who personally manage diligence often let the business slip, and a sagging trailing twelve months during diligence is the classic trigger for a repriced deal.

## The preparation that prevents all three

Experienced advisors push sellers to build the data room before going to market: the standard document set, organized, current, and pre-reviewed by the seller's own CPA and attorney, in effect running diligence on yourself first. Sellers who do this answer requests in days, control the narrative on every known issue, and keep running the business while advisors manage the process. Diligence still will not be enjoyable. It will be short, and short diligence closes deals.`,
    FAQ: [
        { question: "How long does due diligence take when selling a business?", answer: "Commonly 30 to 90 days after the letter of intent for small and mid-sized businesses, longer when records are disorganized or financing is complex. Seller preparation is the biggest controllable factor in the timeline." },
        { question: "Can a buyer lower the price during due diligence?", answer: "Yes. Retrading, revising terms based on diligence findings, is common when material surprises emerge. Complete, organized disclosure up front is the seller's best protection against it." },
        { question: "Should I disclose problems to the buyer?", answer: "Material issues should be disclosed, on advice of your attorney, and framed with context and remediation. Buyers price disclosed problems; discovered problems make them re-examine everything else you told them." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Exit Planning",
            title: "How Long Does It Take to Sell a Business?",
            meta: "3 min · Brief 43",
            to: "/journals/how-long-does-it-take-to-sell-a-business",
        },
        {
            tag: "Deal Process",
            title: "Asset Sale vs Stock Sale: What Every Seller Should Understand Before Negotiating",
            meta: "3 min · Brief 46",
            to: "/journals/asset-sale-vs-stock-sale",
        },
        {
            tag: "Value Drivers",
            title: "Why Clean Financial Records Are Worth Real Money When You Sell",
            meta: "3 min · Brief 51",
            to: "/journals/clean-financial-records-business-value",
        },
    ],
};

export default BRIEF;
